import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import Select, { components, StylesConfig, InputProps, OptionProps, SingleValueProps, MultiValueProps, MenuProps } from 'react-select';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '../UserAvatar';
import { UserDetailsCard } from './UserDetailsCard';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { platformService } from '../../services/platform.service';
import './TikitPeoplePicker.css';
import { MeToken, PeoplePicker_UserSourceOptions, ScopeTokens, UserSource} from '../../utils/constants';
import { getClaims } from '../../../AuthConfig';
import { PickCounts, pickCountsKey, rankByPicks, readPickCounts, recordPicks } from './peoplePickerRanking';

const SelectClearContext = createContext<{ clearSelection: () => void }>({ clearSelection: () => {} });

export interface TikitPeoplePickerProps {
  options?: PlatformUser[];
  value?: PlatformUser[];
  onChange?: (selected: PlatformUser[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isMultiple?: boolean;
  isClearable?: boolean;
  noOptionsMessage?: string;
  globalTheme?: any;
  source?: number; // Use the enum value from PeoplePicker_UserSourceOptions
  isInvertField?: boolean;
  menuPortalBehavior?: 'overlay' | 'body' | 'none';
  // Pins a "Me" pseudo-user (ScopeTokens.MeTokenPlatformUser) at the top of the default
  // list, and surfaces it again when the search text is exactly the word "me" - matched
  // on the literal word, case insensitive, same as the picker this superseded. #35080
  enableMeToken?: boolean;
}

interface TikitPeoplePickerOption {
  label: string;
  value: string;
  user: PlatformUser;
}

const getOptionValue = (user: PlatformUser) => {
  return user.AadObjectId || user.UserName || String(user.Id);
};

// The Me scope token is matched on the literal word "me", case insensitive, regardless
// of locale - it is the key custom-view filters are saved against. #35080
const isMeSearch = (text: string) => (text ?? "").trim().toLowerCase() == MeToken.toLowerCase();
const isMeUser = (user: PlatformUser) => user?.AadObjectId === ScopeTokens.MeTokenPlatformUser.AadObjectId;

const buildOptions = (users: PlatformUser[]) => {
  return users.filter(u => u != undefined && (u?.Id ?? 0) != -1).map((user) => ({
    label: user.FullName || user.Email || user.UserName,
    value: getOptionValue(user),
    user
  }));
};

const SUGGESTION_LIMIT = 5;

// Only the dropdown's suggestion list is capped - the selected value itself (chips/
// controlled value) must go through buildOptions uncapped, or a 6th pick would never appear.
const buildSuggestionOptions = (users: PlatformUser[]) => buildOptions(users).slice(0, SUGGESTION_LIMIT);

// Picks are remembered per signed-in user, so two agents sharing a browser keep their own ranking. #35465
const currentPickCountsKey = () => {
  try {
    return pickCountsKey(getClaims('tid')[0], getClaims('oid')[0]);
  } catch {
    return '';
  }
};

const avatarSize = 'small';

const UserOption = (props: OptionProps<TikitPeoplePickerOption, true>) => {
  const { user } = props.data;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px' }}>
        <div style={{ flexShrink: 0 }} className="tikit-avatar-wrapper">
          <UserAvatar idOrUpn={user.AadObjectId ?? user.UserName} avatarProps={{ name: user.FullName || user.UserName, size: avatarSize }} />
        </div>
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.FullName || user.UserName}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--mgt-theme-subtle-text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.JobTitle}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const ValueChip = ({
  user,
  label,
  onClear,
  removeButtonProps
}: {
  user: PlatformUser;
  label: string;
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  removeButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClear?.(event);
  };

  // Stopping mousedown keeps react-select from opening the menu, so a click only opens the card.
  const identity = (
    <span
      style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, cursor: 'pointer' }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '50%' }}>
        <UserAvatar idOrUpn={user.AadObjectId ?? user.UserName} avatarProps={{ name: user.FullName || user.UserName, size: 'smaller' }} />
      </div>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </span>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginRight: 6,
        padding: '2px 0px 2px 2px',
        borderRadius: 16,
        backgroundColor: 'var(--people-background-color)',
        width: 'fit-content'
      }}
      className="tikit-avatar-wrapper"
    >
      {isMeUser(user) ? identity : <UserDetailsCard user={user} trigger={identity} />}
      <button
        type="button"
        {...removeButtonProps}
        onClick={(event) => {
          handleClick(event);
          removeButtonProps?.onClick?.(event);
        }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          removeButtonProps?.onMouseDown?.(event);
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--mgt-theme-default-foreground)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          fontSize: '14px',
          lineHeight: 1,
          marginLeft: 2,
          marginRight: 6,
          borderRadius: 2,
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(event) => {
          (event.target as HTMLElement).style.backgroundColor = 'transparent';
        }}
        onMouseLeave={(event) => {
          (event.target as HTMLElement).style.backgroundColor = 'transparent';
        }}
        title="Clear selection"
      >
        ✕
      </button>
    </div>
  );
};

const MultiValue = (props: MultiValueProps<TikitPeoplePickerOption, true>) => {
  const { data, removeProps } = props;
  return <ValueChip user={data.user} label={data.label} removeButtonProps={removeProps as React.ButtonHTMLAttributes<HTMLButtonElement>} />;
};

const SingleValue = ({ data }: SingleValueProps<TikitPeoplePickerOption, true>) => {
  const { clearSelection } = useContext(SelectClearContext);
  return <ValueChip user={data.user} label={data.label} onClear={() => clearSelection()} />;
};

const HiddenInput = (props: InputProps<TikitPeoplePickerOption, any>) => {
  if (!props.isMulti && props.hasValue) return null;
  return <components.Input {...props} />;
};

const Menu = (props: MenuProps<TikitPeoplePickerOption, true>) => {
  return (
    <components.Menu
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (event) => {
          event.stopPropagation();
          props.innerProps?.onMouseDown?.(event);
        },
        onClick: (event) => {
          event.stopPropagation();
          props.innerProps?.onClick?.(event);
        },
        onTouchStart: (event) => {
          event.stopPropagation();
          props.innerProps?.onTouchStart?.(event);
        }
      }}
    />
  );
};

const selectStyles: StylesConfig<TikitPeoplePickerOption, true> = {
  control: (styles) => ({
    ...styles,
    minHeight: 34,
    borderRadius: 4,
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'var(--input-background-color)',
    color: 'var(--mgt-theme-default-foreground)',
    '&:hover': {
      border: 'none'
    }
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: '4px 8px'
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--mgt-theme-default-foreground)',
    margin: 0,
    padding: 0
  }),
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--mgt-theme-default-foreground)',
    opacity: 0.7
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'var(--mgt-theme-default-foreground)'
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: 0,
    color: 'transparent',
    visibility: 'hidden'
  }),
  clearIndicator: (styles) => ({
    ...styles,
    display: 'none'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  option: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: isFocused ? 'var(--mgt-theme-background-brand-hover1)' : 'var(--mgt-theme-background1)',
    color: 'var(--mgt-theme-default-foreground)',
    cursor: 'pointer'
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'var(--mgt-theme-background1)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.16)',
    borderRadius: 6,
    overflow: 'hidden'
  }),
  menuList: (styles) => ({
    ...styles,
    backgroundColor: 'var(--mgt-theme-background1)'
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 2147483647
  }),
  multiValue: (styles) => ({
    ...styles,
    borderRadius: 16,
    backgroundColor: 'var(--mgt-theme-background)'
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    display: 'flex',
    alignItems: 'center',
    padding: '2px 6px',
    overflow: 'hidden'
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: 'var(--mgt-theme-default-foreground)',
    ':hover': {
      backgroundColor: 'transparent',
      color: 'var(--mgt-theme-default-foreground)'
    }
  })
};

// The preloaded options are every licensed user, and archiving keeps the licence, so an
// archived agent would otherwise be suggested before anything is typed. The typed search
// already drops them server-side.
const filterPickerOptions =(options: PlatformUser[], source?: number): PlatformUser[] => {
  const active = options.filter((opt) => !opt.IsArchived);
  switch (source) {
    case PeoplePicker_UserSourceOptions.EntraMembers:
      return active.filter((opt) => opt.Source === UserSource.Entra && opt.IsExternalUser === false);
    case PeoplePicker_UserSourceOptions.EntraGuests:
      return active.filter((opt) => opt.Source === UserSource.Entra && opt.IsExternalUser === true);
    case PeoplePicker_UserSourceOptions.EntraAll:
      return active.filter((opt) => opt.Source === UserSource.Entra);
    case PeoplePicker_UserSourceOptions.Tikit:
      return active.filter((opt) => opt.Source === UserSource.Tikit);
    default:
      return active;
  }
};

export const TikitPeoplePicker = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Start typing a name...',
  isDisabled = false,
  noOptionsMessage = 'No results found',
  isMultiple = false,
  isClearable = false,
  globalTheme,
  source,
  isInvertField,
  menuPortalBehavior = 'overlay',
  enableMeToken = false
}: TikitPeoplePickerProps) => {
  const { t } = useTranslation();
  const [searchResults, setSearchResults] = useState<PlatformUser[]>([]);
  const [defaultUsers, setDefaultUsers] = useState<PlatformUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputText, setInputText] = useState('');
  const apiRef = useRef(new platformService());
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState<any>(undefined);
  const filteredOptions = filterPickerOptions(options, source);
  const pickCountsKeyRef = useRef(currentPickCountsKey());
  const [pickCounts, setPickCounts] = useState<PickCounts>(() => readPickCounts(localStorage, pickCountsKeyRef.current));

  // ScopeTokens.MeTokenPlatformUser has its own `selected` field that PlatformUser doesn't
  // declare, so neither type is assignable to the other and a direct `as` is rejected -
  // go through `unknown` first, same as elsewhere this sentinel is reused as a PlatformUser.
  const meTokenPlatformUser = ScopeTokens.MeTokenPlatformUser as unknown as PlatformUser;

  const buildMeOption = () => ({
    label: t('common.me'),
    value: getOptionValue(meTokenPlatformUser),
    user: meTokenPlatformUser
  });

  const trimmedInput = inputText.trim();
  const isMeSearchActive = trimmedInput !== '' && isMeSearch(trimmedInput);
  // react-select's isMulti selectOption toggles: clicking an option that's already in `value`
  // removes it instead of adding it. hideSelectedOptions is off (below) to allow custom
  // rendering of the already-selected state, so the unfiltered list otherwise lets a user click
  // an already-selected recent/default result and have it silently deselect for multi-select,
  // or just re-fire the same selection for single-select. Excluding already-selected users
  // before the top-4 slice in buildOptions is what stops that, without shrinking the list below
  // 4 candidates.
  const selectedValueKeys = new Set(value.map(getOptionValue));
  const excludeSelected = (users: PlatformUser[]) =>
    users.filter(u => !selectedValueKeys.has(getOptionValue(u)));
  // A remembered pick may have been found by a typed search, outside the preloaded options, so it is
  // held to the same source and archive rules - judged on the preloaded copy when there is one, since
  // the remembered copy is only as fresh as the pick.
  const optionsByKey = new Map(options.map((u) => [getOptionValue(u), u]));
  const isAllowedPick = (u: PlatformUser) => filterPickerOptions([optionsByKey.get(getOptionValue(u)) ?? u], source).length > 0;
  const defaultSuggestions = rankByPicks(filteredOptions.length > 0 ? filteredOptions : defaultUsers, pickCounts, getOptionValue, isAllowedPick);
  // A 2-letter "me" query never reaches the backend (searches need 3+ chars), so there's
  // no real search result set to show alongside it - keep the list focused on just Me.
  const baseOptions = isMeSearchActive
    ? []
    : buildSuggestionOptions(excludeSelected(searchResults.length > 0 ? searchResults : defaultSuggestions));

  const showMeToken = enableMeToken
    && (trimmedInput === '' || isMeSearchActive)
    && !value.some(isMeUser);
  const selectOptions = showMeToken
    ? [buildMeOption(), ...baseOptions.filter(o => !isMeUser(o.user))]
    : baseOptions;
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  useEffect(() => {
    // buildOptions drops the Me pseudo-user (it filters out Id === -1 like any other
    // sentinel/unassigned entry), so a saved "Me" selection has to be re-added here.
    const meSelected = enableMeToken && value.some(isMeUser);
    const withoutMe = value.filter(u => !isMeUser(u));
    setSelectedOptions(meSelected ? [buildMeOption(), ...buildOptions(withoutMe)] : buildOptions(value));
  }, [value, enableMeToken, t]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (menuPortalBehavior === 'none') {
      setMenuPortalTarget(undefined);
      return;
    }

    if (menuPortalBehavior === 'body') {
      setMenuPortalTarget(document.body);
      return;
    }

    // Prefer the overlay root and avoid content nodes that may clip overflow.
    const dialogRoot = containerRef.current?.closest('.ui-dialog') ?? null;
    const popupRoot = containerRef.current?.closest('.ui-popup') ?? null;
    const roleDialog = containerRef.current?.closest('[role="dialog"]') ?? null;
    setMenuPortalTarget(dialogRoot ?? popupRoot ?? roleDialog ?? undefined);
  }, [menuPortalBehavior]);

  useEffect(() => {
    // Only fetch defaults once on initialization; kept separate from searchResults so it survives blur.
    // Use filteredOptions (not raw options) so pickers whose options are filtered down to zero by source still get defaults.
    if (filteredOptions.length === 0) {
      const fetchDefaultUsers = async () => {
        try {
          setIsSearching(true);
          const response = await apiRef.current.searchUsersFilterBySource(
            '',
            PeoplePicker_UserSourceOptions[source] ?? PeoplePicker_UserSourceOptions[PeoplePicker_UserSourceOptions.All]
          );
          const users = JSON.parse(response.data?.value ?? '[]') as PlatformUser[];
          setDefaultUsers(users);
        } catch (error) {
          console.error('Error fetching default people picker users:', error);
          setDefaultUsers([]);
        } finally {
          setIsSearching(false);
        }
      };

      fetchDefaultUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const rememberPicks = (selected: PlatformUser[]) => {
    const picks = selected
      .filter((u) => !isMeUser(u) && !selectedValueKeys.has(getOptionValue(u)))
      .map((user) => ({ key: getOptionValue(user), user }));
    if (picks.length > 0) setPickCounts(recordPicks(localStorage, pickCountsKeyRef.current, picks));
  };

  const handleChange = (items: TikitPeoplePickerOption[] | TikitPeoplePickerOption | null) => {
    if (isMultiple) {
      const selected = items ? (items as TikitPeoplePickerOption[]).map((item) => item.user) : [];
      rememberPicks(selected);
      onChange?.(selected);
    } else {
      const selected = items ? [(items as TikitPeoplePickerOption).user] : [];
      rememberPicks(selected);
      onChange?.(selected);
    }
  };

  const handleInputChange = (inputValue: any, actionMeta: any) => {
    // Ensure inputValue is a string
    const searchValue = typeof inputValue === 'string' ? inputValue : '';

    if (actionMeta.action === 'input-change') {
      setInputText(searchValue);

      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      const trimmedValue = searchValue.trim();
      if (trimmedValue.length < 3) {
        // Less than 3 characters, clear results
        setSearchResults([]);
        return;
      }

      // Set new timeout - only search after user stops typing (500ms delay)
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          setIsSearching(true);
          const response = await apiRef.current.searchUsersFilterBySource(trimmedValue, PeoplePicker_UserSourceOptions[source] ?? PeoplePicker_UserSourceOptions[PeoplePicker_UserSourceOptions.All]);
          const users = JSON.parse(response.data?.value ?? '[]') as PlatformUser[];
          setSearchResults(users);
        } catch (error) {
          console.error("Error searching users:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500); // Wait 500ms after user stops typing
    }
  };

  const handleClearSelection = () => {
    selectRef.current?.clearValue?.();
  };

  const handleBlur = () => {
    // Clear search results but preserve selected value
    setSearchResults([]);
    setInputText('');
  };

  const themeStyle: any = globalTheme ? ThemeColorScheme(globalTheme.siteVariables) : {};
  const extraStyles: any = { 
    '--input-background-color': isInvertField ? 'var(--mgt-theme-background)' : 'var(--mgt-theme-background2)', 
    '--people-background-color': isInvertField ? 'var(--mgt-theme-background2)' : 'var(--mgt-theme-background)' 
  };

  const mergedStyles: StylesConfig<TikitPeoplePickerOption, true> = {
    ...selectStyles, 
    control: (styles, state) => {
      const base = (selectStyles.control as any)(styles, state);
      return { ...base, ...extraStyles, ...themeStyle };
    },
    menu: (styles) => {
      const base = (selectStyles.menu as any)(styles);
      return { ...base, ...themeStyle };
    },
    menuList: (styles) => {
      const base = (selectStyles.menuList as any ? (selectStyles.menuList as any)(styles) : styles);
      return { ...base, ...themeStyle };
    },
    menuPortal: (styles) => ({
      ...styles,
      ...themeStyle,
      zIndex: 2147483647
    })
  };

  return (
    <SelectClearContext.Provider value={{ clearSelection: handleClearSelection }}>
      <div className="w-full"
        ref={containerRef}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        <Select
          ref={selectRef}
          classNamePrefix='tikit-people-picker'
          isMulti={isMultiple}
          isDisabled={isDisabled}
          isClearable={!isMultiple && isClearable}
          options={selectOptions}
          {...(selectedOptions?.length ?? 0) > 0 && { value: selectedOptions }}
          onChange={handleChange}
          onBlur={handleBlur}
          onMenuOpen={() => setPickCounts(readPickCounts(localStorage, pickCountsKeyRef.current))}
          onInputChange={handleInputChange}
          placeholder={placeholder}
          noOptionsMessage={() => noOptionsMessage}
          maxMenuHeight={420}
          styles={mergedStyles}
          components={{ Option: UserOption, MultiValue, SingleValue, Input: HiddenInput, Menu }}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
          closeMenuOnSelect={true}
          hideSelectedOptions={false}
          getOptionValue={(option) => option.value}
          filterOption={() => true}
          isLoading={isSearching}
        />
      </div>
    </SelectClearContext.Provider>
  );
};
